import { patchJson, putJson } from '@/utils/fetchOptions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { logout } from '@/utils/auth';
import { message } from 'antd/lib';
import { useTranslation } from 'react-i18next';
import { useAuthToken } from '../useAuth';

export type DraftDataMedia = Pick<
  DraftMedia,
  'cloudinary_id' | 'is_raw' | 'resource_type' | 'filename'
>;
interface DraftData {
  medias: DraftDataMedia[]; // Cloudinary image URLs
  caption: string;
  permalink?: string;
  type?: string;
}

export function useDraftUpdate(draftId?: string) {
  const queryClient = useQueryClient();
  const token = useAuthToken();
  const { t } = useTranslation();

  const draftMutation = useMutation({
    mutationKey: ['update-draft', draftId],
    mutationFn: async ({ caption, medias, permalink, type = 'update' }: DraftData) => {
      if (type === 'create') {
        return putJson({
          url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/influencer/draft/${draftId}/create`,
          body: {
            medias,
            caption,
            permalink
          },
          token
        }).then((response: { draft: Api.Influencer.Draft } | ResponseObject) => {
          if ((response as ResponseExpiredToken).isExpired) {
            logout();
          }

          if ('code' in response && !['DRAFT_CREATED', 'DRAFT_UPDATED'].includes(response.code)) {
            message.error(t(`alert.${response.code}`));
          } else {
            message.success(t(`alert.SAVED`));
          }
          return response;
        });
      }
      return patchJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/influencer/draft/${draftId}/update`,
        body: {
          medias,
          caption,
          permalink
        },
        token
      }).then((response: { draft: Api.Influencer.Draft } | ResponseObject) => {
        if ((response as ResponseExpiredToken).isExpired) {
          logout();
        }

        if ('code' in response && !['DRAFT_CREATED', 'DRAFT_UPDATED'].includes(response.code)) {
          message.error(t(`alert.${response.code}`));
        } else {
          message.success(t(`alert.SAVED`));
        }
        return response;
      });
    },
    onSuccess: (data) => {
      if ('draft' in data) {
        const key = `influencer-collaboration-${data.draft?.collaboration_id}-${data.draft.beat_id}-drafts`;
        queryClient.setQueryData([key, null], (oldItems: TypeAny) => {
          if (oldItems?.drafts) {
            const res = oldItems.drafts.map((item: Api.Influencer.Draft) =>
              item.product_id === data.draft.product_id ? data.draft : item
            );

            return { drafts: res };
          }
          return { drafts: [] };
        });
      }
    }
  });

  const updateDraft = (draftData: DraftData) => {
    return draftMutation.mutateAsync(draftData);
  };

  return {
    updateDraft,
    isLoading: draftMutation.isLoading,
    error: draftMutation.error,
    isSuccess: draftMutation.isSuccess
  };
}
